import { useEffect, useMemo, useRef } from "react";
import { classNames } from "maven-lib/dist/utils/misc";
import { v4 as uuidv4 } from "uuid";

interface HbCodeInputProps {
  length: 4 | 5 | 6 | 7 | 8;
  code: string;
  onValueChange?: (code: string) => void;
  onInputCompleted?: (code: string) => void;
}

export function HbCodeInput(props: HbCodeInputProps) {
  const { code, onValueChange, length } = props;

  const rootEl = useRef(null);
  const uuid = useMemo(() => uuidv4(), []);

  /** props.length 에 맞게 code 길이를 맞추는 과정 */
  function refineCode(raw: string) {
    return raw.length < props.length ? raw.padEnd(props.length) : raw.substring(0, props.length);
  }

  /**
   * @param source 변경 이전 code string
   * @param changedDigit 변경된 자리의 글자
   * @param changedIndex 변경된 자리의 index
   */
  function handleCodeChange(source: string, changedDigit: string, changedIndex: number) {
    // NOTE: 자동 입력 기능 처리
    if (changedDigit.length === length) {
      onValueChange(changedDigit);
      return;
    }

    // NOTE: 값을 채울경우 커서를 다음칸으로, 지울경우 전칸으로 포커스 보냄
    const inputs = rootEl.current.parentElement.getElementsByTagName("input");

    if (changedDigit === "") inputs[Math.max(changedIndex - 1, 0)]?.focus();
    else inputs[Math.min(changedIndex + 1, props.length)]?.focus();

    const spaceRestoredDigit = changedDigit === "" ? " " : changedDigit.substring(0, 1);
    const newPropsCode = source.substring(0, changedIndex) + spaceRestoredDigit + source.substring(changedIndex + 1);

    onValueChange(newPropsCode);
  }

  function handleCodePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();

    const pastedCode = e.clipboardData.getData("text");
    const refinedCode = refineCode(pastedCode);

    onValueChange(refinedCode);
  }

  useEffect(() => {
    if (code.trim().replace(" ", "").length === props.length) {
      props.onInputCompleted?.(code);
    }
  }, [code, props.length, props]);

  /** 하위 컴포넌트에 할당할 code string */
  const refinedCode = refineCode(code);

  return (
    <div className={classNames("hb-code-input", `--length-${length}`)} ref={rootEl}>
      {Array(length)
        .fill("*")
        .map((_, i) => (
          <div key={i}>
            <input
              id={`hb-code-input--${uuid}-${i}`}
              value={refinedCode[i] === " " ? "" : refinedCode[i]}
              type="tel"
              onFocus={(evt) => evt.target.select()}
              onChange={(evt) => handleCodeChange(refinedCode, evt.target.value, i)}
              onPaste={(evt) => handleCodePaste(evt)}
            />
          </div>
        ))}
    </div>
  );
}
